import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, Pencil, ShieldOff, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';
import { toLocalInputValue } from '../../lib/compose';

const selectClass =
  'h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30';

// Dört sayfa da cPanel'e bağlı: yapılandırılmamışsa açıklayıcı bir durum gösterilir.
export const CpanelGate = ({ title, description, children }) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getMailRulesStatus().then(setStatus).catch((e) => setError(e.message || 'Durum alınamadı'));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">{title}</h2>
        <p className="text-muted-foreground mt-1">{description}</p>
      </div>
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : !status ? (
        <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
      ) : !status.configured ? (
        <div className="rounded-xl border border-border p-6 flex gap-4" data-testid="cpanel-not-configured">
          <Info className="h-5 w-5 shrink-0 text-muted-foreground mt-0.5" />
          <div>
            <p className="font-medium">Bu özellik henüz etkinleştirilmedi</p>
            <p className="text-sm text-muted-foreground mt-1">
              Posta kuralları sunucuda uygulanır. Yöneticiniz posta kuralları bağlantısını yapılandırdığında bu sayfa kullanılabilir olacak.
            </p>
          </div>
        </div>
      ) : (
        children(status)
      )}
    </div>
  );
};

const Spinner = () => <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

// ==================== Filtreler ====================

const FIELD_LABEL = { from: 'Gönderen', to: 'Alıcı', subject: 'Konu', body: 'İçerik' };
const OP_LABEL = { contains: 'içeriyorsa', not_contains: 'içermiyorsa', is: 'eşitse', begins: 'ile başlıyorsa', ends: 'ile bitiyorsa' };

const describeAction = (a, moveFolders) => {
  if (a.type === 'discard') return 'Mesajı sil';
  if (a.type === 'forward') return `${a.value} adresine ilet`;
  if (a.type === 'move') return `"${moveFolders?.[a.value] || a.value}" klasörüne taşı`;
  return 'Bilinmeyen eylem';
};

const emptyFilter = () => ({
  name: '', match_all: true,
  conditions: [{ field: 'from', op: 'contains', value: '' }],
  action: { type: 'discard', value: '' },
});

const FilterForm = ({ open, onOpenChange, initial, editingName, status, onSaved }) => {
  const [form, setForm] = useState(emptyFilter());
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial || emptyFilter()); }, [open, initial]);

  const setCondition = (i, patch) => setForm((f) => ({ ...f, conditions: f.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, action: { type: form.action.type, value: form.action.type === 'discard' ? null : form.action.value } };
      if (editingName) await api.updateMailFilter(editingName, body);
      else await api.createMailFilter(body);
      toast.success('Filtre kaydedildi');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error('Filtre kaydedilemedi', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const folderKeys = Object.keys(status.move_folders || {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="filter-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{editingName ? 'Filtreyi düzenle' : 'Yeni filtre'}</DialogTitle>
            <DialogDescription>Gelen iletiler koşullara göre otomatik işlenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="filter-name">Filtre adı</Label>
              <Input id="filter-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={60} placeholder="Örn. Reklam iletileri" data-testid="filter-name" />
            </div>

            <div className="space-y-2">
              <Label>Koşullar</Label>
              {form.conditions.length > 1 && (
                <select className={`${selectClass} block`} value={form.match_all ? 'all' : 'any'} onChange={(e) => setForm({ ...form, match_all: e.target.value === 'all' })} data-testid="filter-match">
                  <option value="all">Tüm koşullar sağlanırsa</option>
                  <option value="any">Koşullardan herhangi biri sağlanırsa</option>
                </select>
              )}
              {form.conditions.map((c, i) => (
                <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2" data-testid="filter-condition">
                  <select className={`${selectClass} w-28`} value={c.field} onChange={(e) => setCondition(i, { field: e.target.value })} aria-label="Alan">
                    {Object.entries(FIELD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <select className={`${selectClass} w-36`} value={c.op} onChange={(e) => setCondition(i, { op: e.target.value })} aria-label="Koşul">
                    {Object.entries(OP_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <Input className="flex-1 min-w-[140px]" value={c.value} onChange={(e) => setCondition(i, { value: e.target.value })} maxLength={200} placeholder="Değer" aria-label="Değer" data-testid="filter-value" />
                  {form.conditions.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" aria-label="Koşulu kaldır" onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, idx) => idx !== i) })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {form.conditions.length < 5 && (
                <Button type="button" variant="ghost" size="sm" className="text-primary" onClick={() => setForm({ ...form, conditions: [...form.conditions, { field: 'subject', op: 'contains', value: '' }] })}>
                  <Plus className="h-4 w-4 mr-1" /> Koşul ekle
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label>Eylem</Label>
              <div className="flex flex-wrap sm:flex-nowrap gap-2">
                <select className={`${selectClass} sm:w-56`} value={form.action.type} onChange={(e) => setForm({ ...form, action: { type: e.target.value, value: '' } })} data-testid="filter-action">
                  <option value="discard">Mesajı sil</option>
                  <option value="forward">Başka adrese ilet</option>
                  {status.folder_move && <option value="move">Klasöre taşı</option>}
                </select>
                {form.action.type === 'forward' && (
                  <Input className="flex-1" type="email" value={form.action.value} onChange={(e) => setForm({ ...form, action: { ...form.action, value: e.target.value } })} placeholder="ornek@adres.com" aria-label="Hedef adres" data-testid="filter-forward" />
                )}
                {form.action.type === 'move' && (
                  <select className={`${selectClass} flex-1`} value={form.action.value} onChange={(e) => setForm({ ...form, action: { ...form.action, value: e.target.value } })} aria-label="Klasör">
                    <option value="">Klasör seçin</option>
                    {folderKeys.map((k) => <option key={k} value={k}>{status.move_folders[k]}</option>)}
                  </select>
                )}
              </div>
              {form.action.type === 'discard' && (
                <p className="text-xs text-destructive">Eşleşen iletiler çöp kutusuna bile gitmeden kalıcı olarak silinir.</p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
            <Button type="submit" disabled={saving} data-testid="filter-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Kaydet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FiltersBody = ({ status }) => {
  const [filters, setFilters] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null, editingName: null });

  const load = useCallback(() => {
    api.getMailFilters().then((r) => setFilters(r.filters || [])).catch((e) => { toast.error('Filtreler yüklenemedi', { description: e.message }); setFilters([]); });
  }, []);
  useEffect(load, [load]);

  const remove = async (f) => {
    if (!window.confirm(`"${f.name}" filtresi silinsin mi?`)) return;
    try {
      await api.deleteMailFilter(f.name);
      toast.success('Filtre silindi');
      load();
    } catch (e) {
      toast.error('Silinemedi', { description: e.message });
    }
  };

  return (
    <>
      <div className="flex justify-end">
        <Button onClick={() => setDialog({ open: true, initial: null, editingName: null })} data-testid="new-filter">
          <Plus className="h-4 w-4 mr-2" /> Yeni filtre
        </Button>
      </div>
      {filters === null ? <Spinner /> : filters.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Henüz filtreniz yok.</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {filters.map((f) => (
            <li key={f.name} className="flex items-start gap-4 p-4" data-testid={`filter-${f.name}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{f.name}</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {f.conditions.map((c) => `${FIELD_LABEL[c.field] || c.field} "${c.value}" ${OP_LABEL[c.op] || c.op}`).join(f.match_all ? ' ve ' : ' veya ')}
                  {' → '}{describeAction(f.action, status.move_folders)}
                </p>
                {!f.editable && <p className="text-xs text-muted-foreground mt-1">cPanel'de oluşturulmuş; burada yalnızca silinebilir.</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                {f.editable && (
                  <Button variant="ghost" size="icon" aria-label={`${f.name} filtresini düzenle`} onClick={() => setDialog({ open: true, initial: { name: f.name, match_all: f.match_all, conditions: f.conditions, action: { type: f.action.type, value: f.action.value || '' } }, editingName: f.name })}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" aria-label={`${f.name} filtresini sil`} className="text-destructive" onClick={() => remove(f)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <FilterForm open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} editingName={dialog.editingName} status={status} onSaved={load} />
    </>
  );
};

export const FiltersSection = () => (
  <CpanelGate title="Filtreler ve kurallar" description="Gelen iletileri koşullara göre otomatik silin veya başka bir adrese iletin.">
    {(status) => <FiltersBody status={status} />}
  </CpanelGate>
);

// ==================== Otomatik yanıt ====================

const INTERVALS = [[12, '12 saat'], [24, '1 gün'], [48, '2 gün'], [72, '3 gün'], [168, '1 hafta']];
const toEpoch = (local) => (local ? Math.floor(new Date(local).getTime() / 1000) : null);
const fromEpoch = (epoch) => (epoch ? toLocalInputValue(new Date(epoch * 1000)) : '');

const AutoReplyBody = () => {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [form, setForm] = useState({ subject: 'Otomatik yanıt', body: '', from_name: '', interval_hours: 24, start: '', stop: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getAutoreply().then((r) => {
      setEnabled(r.enabled);
      if (r.config) {
        setForm({ subject: r.config.subject, body: r.config.body, from_name: r.config.from_name || '', interval_hours: r.config.interval_hours, start: fromEpoch(r.config.start), stop: fromEpoch(r.config.stop) });
      }
      setLoaded(true);
    }).catch((e) => { toast.error('Otomatik yanıt yüklenemedi', { description: e.message }); setLoaded(true); });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.setAutoreply({ ...form, interval_hours: Number(form.interval_hours), start: toEpoch(form.start), stop: toEpoch(form.stop) });
      setEnabled(true);
      toast.success(enabled ? 'Otomatik yanıt güncellendi' : 'Otomatik yanıt etkinleştirildi');
    } catch (err) {
      toast.error('Kaydedilemedi', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const disable = async () => {
    try {
      await api.deleteAutoreply();
      setEnabled(false);
      toast.success('Otomatik yanıt kapatıldı');
    } catch (err) {
      toast.error('Kapatılamadı', { description: err.message });
    }
  };

  if (!loaded) return <Spinner />;

  return (
    <form onSubmit={save} className="rounded-xl border border-border p-5 space-y-5 max-w-2xl" data-testid="autoreply-form">
      <div className="flex items-center gap-3">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-secondary text-muted-foreground'}`} data-testid="autoreply-status">
          {enabled ? 'Aktif' : 'Kapalı'}
        </span>
        <p className="text-sm text-muted-foreground">Gelen iletilere belirlediğiniz mesajla otomatik yanıt verilir.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="ar-subject">Konu</Label>
        <Input id="ar-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} maxLength={200} data-testid="ar-subject" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ar-body">Mesaj</Label>
        <textarea id="ar-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={5000} rows={6} placeholder="Şu an ofis dışındayım. ... tarihinde dönüş yapacağım." className="w-full rounded-md border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30" data-testid="ar-body" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="ar-from">Gönderen adı (opsiyonel)</Label>
          <Input id="ar-from" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} maxLength={120} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ar-interval">Aynı kişiye yanıt aralığı</Label>
          <select id="ar-interval" className={`${selectClass} w-full`} value={form.interval_hours} onChange={(e) => setForm({ ...form, interval_hours: e.target.value })}>
            {INTERVALS.map(([h, label]) => <option key={h} value={h}>{label}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ar-start">Başlangıç (opsiyonel)</Label>
          <input id="ar-start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className={`${selectClass} w-full`} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ar-stop">Bitiş (opsiyonel)</Label>
          <input id="ar-stop" type="datetime-local" value={form.stop} onChange={(e) => setForm({ ...form, stop: e.target.value })} className={`${selectClass} w-full`} />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        {enabled && <Button type="button" variant="outline" onClick={disable} data-testid="ar-disable">Kapat</Button>}
        <Button type="submit" disabled={saving || !form.subject.trim() || !form.body.trim()} data-testid="ar-save">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {enabled ? 'Güncelle' : 'Etkinleştir'}
        </Button>
      </div>
    </form>
  );
};

export const AutoReplySection = () => (
  <CpanelGate title="Otomatik yanıt" description="Tatilde veya ofis dışındayken gelen iletilere otomatik yanıt gönderin.">
    {() => <AutoReplyBody />}
  </CpanelGate>
);

// ==================== Yönlendirme ====================

const ForwardingBody = ({ status }) => {
  const keepCopyConfigurable = status?.keep_copy_configurable !== false;
  const sameDomainOnly = Boolean(status?.forward_same_domain_only);
  const [state, setState] = useState(null); // { destinations, keep_copy }
  const [dest, setDest] = useState('');
  const [keepCopyPref, setKeepCopyPref] = useState(true); // ilk yönlendirme eklenirken kullanılır
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getForwarding().then(setState).catch((e) => { toast.error('Yönlendirme yüklenemedi', { description: e.message }); setState({ destinations: [], keep_copy: false }); });
  }, []);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      setState(await fn());
      if (success) toast.success(success);
      return true;
    } catch (err) {
      toast.error('İşlem başarısız', { description: err.message });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    // İlk hedefte kullanıcının tercihi (varsayılan açık), sonrakilerde sunucudaki mevcut durum
    const keep = state.destinations.length === 0 ? keepCopyPref : state.keep_copy;
    if (await run(() => api.addForwarding(dest.trim(), keep), 'Yönlendirme eklendi')) setDest('');
  };

  if (!state) return <Spinner />;

  return (
    <div className="space-y-5 max-w-2xl">
      {sameDomainOnly && (
        <p className="text-xs text-muted-foreground" data-testid="forward-same-domain-note">
          Güvenlik nedeniyle yalnızca kendi alan adınızdaki adreslere yönlendirme yapılabilir. İletilerin kopyası bu posta kutusunda da kalır.
        </p>
      )}

      <form onSubmit={add} className="flex gap-2" data-testid="forward-form">
        <Input type="email" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Yönlendirilecek e-posta adresi" aria-label="Hedef adres" data-testid="forward-input" />
        <Button type="submit" disabled={busy || !dest.trim()} data-testid="forward-add"><Plus className="h-4 w-4 mr-1" /> Ekle</Button>
      </form>

      {state.destinations.length === 0 ? (
        <p className="text-sm text-muted-foreground">Yönlendirme yok. Gelen iletiler yalnızca bu hesaba teslim edilir.</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border" data-testid="forward-list">
          {state.destinations.map((d) => (
            <li key={d} className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm truncate">{d}</span>
              <Button variant="ghost" size="icon" className="text-destructive" aria-label={`${d} yönlendirmesini kaldır`} disabled={busy} onClick={() => run(() => api.removeForwarding(d), 'Yönlendirme kaldırıldı')}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {keepCopyConfigurable && (
        <div className="flex items-start gap-3 rounded-xl border border-border p-4">
          <Switch
            checked={state.destinations.length > 0 ? state.keep_copy : keepCopyPref}
            disabled={busy}
            onCheckedChange={(v) => (state.destinations.length > 0 ? run(() => api.setForwardingKeepCopy(v)) : setKeepCopyPref(v))}
            aria-label="Kopyasını bu hesapta da tut"
            data-testid="keep-copy"
          />
          <div>
            <p className="text-sm font-medium">Kopyasını bu hesapta da tut</p>
            <p className="text-xs text-muted-foreground mt-0.5">Kapalıysa iletiler yalnızca yönlendirilen adreslere gider, bu posta kutusuna gelmez.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const ForwardingSection = () => (
  <CpanelGate title="Yönlendirme" description="Gelen iletileri başka e-posta adreslerine otomatik iletin.">
    {(status) => <ForwardingBody status={status} />}
  </CpanelGate>
);

// ==================== Engellenen adresler ====================

const BlockedBody = () => {
  const [blocked, setBlocked] = useState(null);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getBlocked().then((r) => setBlocked(r.blocked || [])).catch((e) => { toast.error('Liste yüklenemedi', { description: e.message }); setBlocked([]); });
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.addBlocked(address.trim());
      setBlocked(r.blocked);
      setAddress('');
      toast.success('Adres engellendi');
    } catch (err) {
      toast.error('Engellenemedi', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (addr) => {
    setBusy(true);
    try {
      setBlocked((await api.removeBlocked(addr)).blocked);
      toast.success('Engel kaldırıldı');
    } catch (err) {
      toast.error('Kaldırılamadı', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!blocked) return <Spinner />;

  return (
    <div className="space-y-5 max-w-2xl">
      <form onSubmit={add} className="flex gap-2" data-testid="blocked-form">
        <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="kisi@ornek.com veya ornek.com" aria-label="Engellenecek adres" data-testid="blocked-input" />
        <Button type="submit" disabled={busy || !address.trim()} data-testid="blocked-add"><ShieldOff className="h-4 w-4 mr-1" /> Engelle</Button>
      </form>
      <p className="text-xs text-muted-foreground">Bir alan adı (ornek.com) girerseniz o alan adından gelen tüm iletiler engellenir. Engellenen iletiler silinir.</p>

      {blocked.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Engellenen adres yok.</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border" data-testid="blocked-list">
          {blocked.map((a) => (
            <li key={a} className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sm truncate">{a}</span>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => remove(a)}>Engeli kaldır</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const BlockedSection = () => (
  <CpanelGate title="Engellenen adresler" description="Belirli gönderenlerden gelen iletileri engelleyin.">
    {() => <BlockedBody />}
  </CpanelGate>
);
